import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    NUC_BUTTON_TYPE,
    NUC_FULL_MODAL_DATA,
    NucButtonConfig,
    NucDropdownComponent,
    NucDropdownItem,
    NucFormFieldComponent,
    NucFullModalActionModel,
    NucFullModalService,
    NucInputComponent
} from '@relayter/rubber-duck';
import { finalize, map } from 'rxjs';
import { TeamService } from '../../../api/services/team.service';
import { Toaster } from '../../../classes/toaster.class';
import { RATE_LIMIT_GROUPS, RateLimitModel, TeamModel, TeamPatchModel } from '../../../models/response/team.model';

export interface IEditTeamRateLimitData {
    team: TeamModel;
    rateLimit: RateLimitModel;
}

class TeamRateLimitForm {
    group: FormControl<RATE_LIMIT_GROUPS>;
    rps: FormControl<number>;
    burstRps: FormControl<number>;
    burstDuration: FormControl<number>;
}

@Component({
    selector: 'om-edit-team-usage',
    templateUrl: './edit-team-rate-limits.component.html',
    styleUrls: ['./edit-team-rate-limits.component.scss'],
    imports: [NucInputComponent, NucDropdownComponent, ReactiveFormsModule, NucFormFieldComponent]
})
export class EditTeamRateLimitsComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly teamService = inject(TeamService);
    private readonly fullModalService = inject(NucFullModalService);
    private readonly modalData = inject<IEditTeamRateLimitData>(NUC_FULL_MODAL_DATA);

    public formGroup: FormGroup<TeamRateLimitForm>;
    private saveConfig: NucButtonConfig;

    public rateLimitGroups: NucDropdownItem[] = Object.entries(RATE_LIMIT_GROUPS).map(([key, label]) => new NucDropdownItem(label, key));

    public ngOnInit(): void {
        this.initModalButtons();
        this.initForm();
    }

    private initForm(): void {
        this.formGroup = new FormGroup<TeamRateLimitForm>({
            group: new FormControl({ value: this.modalData.rateLimit.group, disabled: true }, [Validators.required]),
            rps: new FormControl(this.modalData.rateLimit.rps, [Validators.required, Validators.min(0)]),
            burstRps: new FormControl(this.modalData.rateLimit.burst.rps, [Validators.required, Validators.min(0)]),
            burstDuration: new FormControl(this.modalData.rateLimit.burst.duration, [Validators.required, Validators.min(0)])
        });

        this.formGroup.statusChanges
            .pipe(
                map((status) => status === 'VALID'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((valid) => {
                this.saveConfig.disabled = !valid;
            });
    }

    private initModalButtons(): void {
        const cancelConfig = new NucButtonConfig(NUC_BUTTON_TYPE.SECONDARY, 'Cancel');
        this.saveConfig = new NucButtonConfig(NUC_BUTTON_TYPE.PRIMARY, 'Save', false, false, true);

        const cancelAction = new NucFullModalActionModel(cancelConfig);
        const saveAction = new NucFullModalActionModel(this.saveConfig);

        cancelAction.observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.fullModalService.close(false, true));
        saveAction.observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.saveTeam());
        this.fullModalService.setModalActions([cancelAction, saveAction]);
    }

    private saveTeam(): void {
        this.saveConfig.loading = true;
        const newRateLimit = new RateLimitModel(this.modalData.rateLimit.group);
        newRateLimit.rps = this.formGroup.value.rps;
        newRateLimit.burst.rps = this.formGroup.value.burstRps;
        newRateLimit.burst.duration = this.formGroup.value.burstDuration;

        const patchBody = new TeamPatchModel();
        const existingIndex = this.modalData.team.rateLimits.findIndex((rateLimit: RateLimitModel) => rateLimit.group === newRateLimit.group);
        const updatedRateLimits: RateLimitModel[] = this.modalData.team.rateLimits;
        if (existingIndex !== -1) {
            updatedRateLimits[existingIndex] = newRateLimit;
        } else {
            updatedRateLimits.push(newRateLimit);
        }
        patchBody.rateLimits = updatedRateLimits;

        this.teamService
            .patchTeam(this.modalData.team._id, patchBody)
            .pipe(
                finalize(() => {
                    this.saveConfig.loading = false;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result) => {
                    Toaster.success(`Team rate limit for ${newRateLimit.group} is successfully updated!`);
                    this.fullModalService.close(result);
                },
                error: Toaster.handleApiError
            });
    }
}
